const APP_BACKEND_URL = process.env.REACT_APP_APP_BACKEND_URL || 'http://localhost:3001';

export const PUBLIC_EXPLORER_FREE_LIMIT = 10;

export type PublicExplorerRankingMode = 'programs' | 'channels';

export const PUBLIC_EXPLORER_PROGRAMS_SORTS = ['viewsPerEp', 'interactionsPerEp'] as const;
export const PUBLIC_EXPLORER_CHANNELS_SORTS = ['totalViews', 'totalInteractions'] as const;

type ProgramsSortField = (typeof PUBLIC_EXPLORER_PROGRAMS_SORTS)[number];
type ChannelsSortField = (typeof PUBLIC_EXPLORER_CHANNELS_SORTS)[number];
export type PublicExplorerSortField = ProgramsSortField | ChannelsSortField;

export const PUBLIC_EXPLORER_PROGRAMS_DEFAULT_SORT: PublicExplorerSortField = 'viewsPerEp';
export const PUBLIC_EXPLORER_CHANNELS_DEFAULT_SORT: PublicExplorerSortField = 'totalViews';

export function getPublicExplorerDefaultSortForMode(
    mode: PublicExplorerRankingMode
): PublicExplorerSortField {
    return mode === 'channels'
        ? PUBLIC_EXPLORER_CHANNELS_DEFAULT_SORT
        : PUBLIC_EXPLORER_PROGRAMS_DEFAULT_SORT;
}

export function isPublicExplorerSortAllowedForMode(
    mode: PublicExplorerRankingMode,
    sortBy: PublicExplorerSortField
): boolean {
    if (mode === 'channels') {
        return (PUBLIC_EXPLORER_CHANNELS_SORTS as readonly string[]).includes(sortBy);
    }
    return (PUBLIC_EXPLORER_PROGRAMS_SORTS as readonly string[]).includes(sortBy);
}

export interface PublicExplorerProgramRow {
    rank: number;
    programId: string;
    name: string;
    imageS3Key: string | null;
    channelTitle: string | null;
    channelImageS3Key: string | null;
    contentCount: number;
    totalViews: number;
    viewsPerEp: number;
    totalInteractions: number;
    interactionsPerEp: number;
}

export interface PublicExplorerProgramsResponse {
    periodLabel: string;
    dateFrom: string;
    dateTo: string;
    rows: PublicExplorerProgramRow[];
    totalCount: number;
}

export interface PublicExplorerChannelRow {
    rank: number;
    channelId: string;
    name: string;
    imageS3Key: string | null;
    /** Programs with episodes published in the period (same value as programCount) */
    contentCount: number;
    /** Distinct non-hidden programs with episodes published in the period */
    programCount: number;
    totalViews: number;
    viewsPerEp: number;
    totalInteractions: number;
    interactionsPerEp: number;
}

export interface PublicExplorerChannelsResponse {
    periodLabel: string;
    dateFrom: string;
    dateTo: string;
    rows: PublicExplorerChannelRow[];
    totalCount: number;
}

export interface PublicExplorerProgramVideosResponse {
    videos: PublicExplorerProgramVideo[];
}

export interface PublicExplorerProgramVideo {
    title: string;
    youtubeId: string | null;
    url: string | null;
    viewsNow: number;
    /** ISO timestamp of the latest daily snapshot used for viewsNow */
    viewsNowUpdatedAt: string | null;
    viewsAtDay7: number;
    publishedAt: string | null;
}

export interface PublicExplorerProgramsParams {
    sortBy?: PublicExplorerSortField;
}

function appendPublicExplorerQueryParams(
    qs: URLSearchParams,
    params: PublicExplorerProgramsParams
): void {
    if (params.sortBy) qs.set('sortBy', params.sortBy);
}

export const CONTENT_CATEGORIES = [
    'finance',
    'tech',
    'business',
    'health',
    'wellness',
    'entertainment',
    'music',
    'news',
    'politics',
    'sports',
    'lifestyle',
    'food',
    'education',
    'science',
    'culture',
    'true_crime',
    'gaming',
    'relationships',
    'other',
] as const;

export const CONTENT_CATEGORY_ALL = '__all__';

export const CONTENT_CATEGORY_LABELS: Record<string, string> = {
    [CONTENT_CATEGORY_ALL]: 'Todas',
    finance: 'Finanzas',
    tech: 'Tecnología',
    business: 'Negocios',
    health: 'Salud',
    wellness: 'Bienestar',
    entertainment: 'Entretenimiento',
    music: 'Música',
    news: 'Noticias',
    politics: 'Política',
    sports: 'Deportes',
    lifestyle: 'Estilo de vida',
    food: 'Comida',
    education: 'Educación',
    science: 'Ciencia',
    culture: 'Cultura',
    true_crime: 'True crime',
    gaming: 'Gaming',
    relationships: 'Relaciones',
    other: 'Otro',
};

export function resolveContentCategoryFilter(prev: string[], next: string[]): string[] {
    const wasAll = prev.includes(CONTENT_CATEGORY_ALL);
    const hasAll = next.includes(CONTENT_CATEGORY_ALL);
    const realCats = next.filter(c => c !== CONTENT_CATEGORY_ALL);

    if (hasAll && !wasAll) return [CONTENT_CATEGORY_ALL];
    if (hasAll && wasAll && realCats.length > 0) return realCats;
    if (realCats.length === 0) return [CONTENT_CATEGORY_ALL];
    return realCats;
}

export async function getPublicExplorerPrograms(
    params: PublicExplorerProgramsParams
): Promise<PublicExplorerProgramsResponse> {
    const qs = new URLSearchParams();
    appendPublicExplorerQueryParams(qs, params);

    const response = await fetch(
        `${APP_BACKEND_URL}/api/public/rankings/explorer-programs?${qs.toString()}`,
        { credentials: 'include', cache: 'no-store' }
    );

    if (!response.ok) {
        throw new Error('Failed to fetch explorer programs ranking');
    }

    return response.json();
}

export async function getPublicExplorerChannels(
    params: PublicExplorerProgramsParams
): Promise<PublicExplorerChannelsResponse> {
    const qs = new URLSearchParams();
    appendPublicExplorerQueryParams(qs, params);

    const response = await fetch(
        `${APP_BACKEND_URL}/api/public/rankings/explorer-channels?${qs.toString()}`,
        { credentials: 'include', cache: 'no-store' }
    );

    if (!response.ok) {
        throw new Error('Failed to fetch explorer channels ranking');
    }

    return response.json();
}

export async function getPublicExplorerProgramVideos(
    programId: string,
    params: PublicExplorerProgramsParams
): Promise<PublicExplorerProgramVideosResponse> {
    const qs = new URLSearchParams({ programId });
    appendPublicExplorerQueryParams(qs, params);

    const response = await fetch(
        `${APP_BACKEND_URL}/api/public/rankings/explorer-program-videos?${qs.toString()}`,
        { credentials: 'include', cache: 'no-store' }
    );

    if (!response.ok) {
        throw new Error('Failed to fetch program videos');
    }

    const data = await response.json();
    return normalizePublicExplorerVideosResponse(data);
}

export async function getPublicExplorerChannelVideos(
    channelId: string,
    params: PublicExplorerProgramsParams
): Promise<PublicExplorerProgramVideosResponse> {
    const qs = new URLSearchParams({ channelId });
    appendPublicExplorerQueryParams(qs, params);

    const response = await fetch(
        `${APP_BACKEND_URL}/api/public/rankings/explorer-channel-videos?${qs.toString()}`,
        { credentials: 'include', cache: 'no-store' }
    );

    if (!response.ok) {
        throw new Error('Failed to fetch channel videos');
    }

    const data = await response.json();
    return normalizePublicExplorerVideosResponse(data);
}

export function getYouTubeUrl(video: PublicExplorerProgramVideo): string | null {
    if (video.url?.trim()) return video.url.trim();
    if (video.youtubeId?.trim()) {
        return `https://www.youtube.com/watch?v=${video.youtubeId.trim()}`;
    }
    return null;
}

function normalizePublicExplorerVideo(raw: Record<string, unknown>): PublicExplorerProgramVideo {
    const viewsAtDay7 = raw.viewsAtDay7;
    const viewsNowUpdatedAt = raw.viewsNowUpdatedAt;
    let viewsNowUpdatedAtIso: string | null = null;
    if (viewsNowUpdatedAt != null) {
        const updatedAtDate =
            viewsNowUpdatedAt instanceof Date
                ? viewsNowUpdatedAt
                : new Date(String(viewsNowUpdatedAt));
        if (!Number.isNaN(updatedAtDate.getTime())) {
            viewsNowUpdatedAtIso = updatedAtDate.toISOString();
        }
    }
    return {
        title: typeof raw.title === 'string' ? raw.title : 'Sin título',
        youtubeId: (raw.youtubeId as string | null) ?? null,
        url: (raw.url as string | null) ?? null,
        viewsNow: Math.round(Number(raw.viewsNow ?? raw.views ?? 0)),
        viewsNowUpdatedAt: viewsNowUpdatedAtIso,
        viewsAtDay7: Math.round(Number(viewsAtDay7 ?? 0)),
        publishedAt: (raw.publishedAt as string | null) ?? null,
    };
}

function normalizePublicExplorerVideosResponse(
    data: PublicExplorerProgramVideosResponse
): PublicExplorerProgramVideosResponse {
    return {
        videos: (data.videos ?? []).map(video =>
            normalizePublicExplorerVideo(video as unknown as Record<string, unknown>)
        ),
    };
}
